import { useRef, useCallback } from 'react';
import * as THREE from "three";
import { ThreeEvent } from "@react-three/fiber";
import { projectToWorld } from '../components/FloorPlan/projectToWorld';
import { updateAlignmentLinesForSelection } from '../components/FloorPlan/updateAligmentLines';
import { editorStore } from '../store/editorStore';
import { floorplannerStore } from '../store/floorplannerStore';
import { AlignmentSnapState, calculateAlignmentSnap } from '../store/snapToOtherObjects';
import {
    isAreaFillMovementLocked,
    isAreaFillSymbolType,
    isRulerLineType,
    isSingleLineType,
    isSymbolType,
    isWallType,
    SingleLineType,
    SymbolType,
    WallConnectionStart
} from '../types/floorplannerTypes';
import { runInAction } from 'mobx';
import { renderStore } from '../store/renderStore';
import { wallGeometryCache } from '../utils/WallGeometryCache';
import { getSelectionBounds, isSelectionBoundsItem } from '../utils/selectionBounds';
import {
    isLoose2DDragCollisionSymbol,
    resolveSymbolGroup2DDragOffset,
} from '../utils/symbol2DDragCollision';

interface SymbolPositionData {
    id: string;
    type: string;
    position: { x: number; y: number };
}

interface LinePositionData {
    id: string;
    type: string;
    start: { x: number; y: number };
    end: { x: number; y: number };
}

type PositionData = SymbolPositionData | LinePositionData;

interface DragState {
    originalPositions: Map<string, { position?: THREE.Vector2, start?: THREE.Vector2, end?: THREE.Vector2 }>;
    currentOffset: THREE.Vector2;
    lastUpdateTime: number;
    isDragging: boolean;
    selectionBounds?: {
        corners: [THREE.Vector2, THREE.Vector2, THREE.Vector2, THREE.Vector2];
        center: THREE.Vector2;
    };
    selectionIds?: Set<string>;
    alignmentSnap?: AlignmentSnapState;
    lastValidCollisionOffset?: THREE.Vector2;
}

type GroupAlignmentSnap = {
    delta: THREE.Vector2;
    axes: number;
    distance: number;
    state: AlignmentSnapState;
};

const getGroupAlignmentSnap = (
    points: THREE.Vector2[],
    previous?: AlignmentSnapState,
): GroupAlignmentSnap | null => {
    let bestSnap: GroupAlignmentSnap | null = null;

    for (const point of points) {
        const pseudoLine = {
            id: "__group-selection__",
            type: "singleLine",
            start: point,
            end: point,
        } as SingleLineType;

        const preview = calculateAlignmentSnap(pseudoLine, WallConnectionStart, point, { previous });
        const axes = (preview.snappedX ? 1 : 0) + (preview.snappedY ? 1 : 0);

        if (preview.snapped && axes > 0) {
            const delta = preview.start.clone().sub(point);
            const distance = delta.length();
            if (!bestSnap || axes > bestSnap.axes || (axes === bestSnap.axes && distance < bestSnap.distance)) {
                bestSnap = {
                    delta,
                    axes,
                    distance,
                    state: {
                        snappedX: preview.snappedX,
                        snappedY: preview.snappedY,
                        targetX: preview.targetX,
                        targetY: preview.targetY,
                    },
                };
            }
        }
    }

    return bestSnap;
};

const RIGID_GROUP_WALL_COUNT = 2;

/**
 * Hook to drag selected objects
 * @param gl The WebGLRenderer
 * @param camera The camera
 * @returns The startDragging function and isDragging boolean
 * @example
 * const { startDragging, isDragging } = useDragSelectedObjects(gl, camera);
 * <Canvas onPointerDown={startDragging} />
 * if (isDragging.current) {
 *    // Do something while dragging
 * }
 */
export const useDragSelectedObjects = (gl: THREE.WebGLRenderer, camera: THREE.Camera) => {
    const initialPointerPosition = useRef<[number, number] | null>(null);
    const dragState = useRef<DragState>({
        originalPositions: new Map(),
        currentOffset: new THREE.Vector2(),
        lastUpdateTime: 0,
        isDragging: false
    });
    const isDraggingGroupSelection = useRef(false);
    const aboutToDrag = useRef(true);
    const rafId = useRef<number | null>(null);
    const draggedWallIds = useRef<string[]>([]);
    const beginWallDragCalled = useRef(false);

    const startDraggingGroupSelection = useCallback(async (event: ThreeEvent<PointerEvent>) => {
        if (floorplannerStore.lockedByOther) return;
        event.stopPropagation();

        // OPTIMIZATION: Prepare IDs and start visual drag IMMEDIATELY to unblock UI
        // Collect all object IDs and wall IDs FIRST
        const objectIds: string[] = [];
        const wallIds: string[] = [];
        draggedWallIds.current = [];

        editorStore.selections.forEach((selection) => {
            // Locked room floors stay put during group drag.
            if (isAreaFillSymbolType(selection)) {
                const liveFill = floorplannerStore.symbolsMap.get(selection.id);
                if (liveFill && isAreaFillSymbolType(liveFill) && isAreaFillMovementLocked(liveFill)) {
                    return;
                }
            }
            objectIds.push(selection.id);
            if (isWallType(selection)) {
                wallIds.push(selection.id);
                draggedWallIds.current.push(selection.id);
            }
        });

        // Nothing movable (e.g. only locked room floors selected).
        if (objectIds.length === 0) {
            return;
        }

        const visualObjectIds = new Set(objectIds);
        if (wallIds.length > 0) {
            const draggedWallIdSet = new Set(wallIds);

            wallIds.forEach((wallId) => {
                const wall = floorplannerStore.wallsMap.get(wallId);
                wall?.symbolAttachments?.forEach((attachment) => {
                    visualObjectIds.add(attachment.symbolId);
                });
            });

            floorplannerStore.symbolsMap.forEach((symbol) => {
                if (symbol.attachedTo && draggedWallIdSet.has(symbol.attachedTo)) {
                    visualObjectIds.add(symbol.id);
                }
            });
        }

        const rigidGroupTranslate = wallIds.length >= RIGID_GROUP_WALL_COUNT;

        // Begin translate mode EARLY - this blocks expensive reactions during setup
        renderStore.beginGroupTranslate(Array.from(visualObjectIds), wallIds);
        floorplannerStore.setDeferRoomUpdates(true);

        // Begin delta-based undo tracking for group drag
        floorplannerStore.beginUndoTransaction('dragGroup');
        const selectedWallIdsAtDragStart = new Set(wallIds);
        // Record all selected objects for undo
        editorStore.selections.forEach((selection) => {
            if (isAreaFillSymbolType(selection)) {
                const liveFill = floorplannerStore.symbolsMap.get(selection.id);
                if (liveFill && isAreaFillSymbolType(liveFill) && isAreaFillMovementLocked(liveFill)) {
                    return;
                }
            }
            if (isSymbolType(selection)) {
                floorplannerStore.recordSymbolForUndo(selection.id);
                const attachedWall = floorplannerStore.symbolIsAttached(selection.id);
                if (attachedWall && !selectedWallIdsAtDragStart.has(attachedWall)) {
                    floorplannerStore.recordWallForUndo(attachedWall);
                }
            } else {
                floorplannerStore.recordObjectForUndo(selection.id);
            }
        });

        const [initialWorldX, initialWorldY] = projectToWorld(
            event.clientX,
            event.clientY,
            gl,
            camera
        );

        initialPointerPosition.current = [initialWorldX, initialWorldY];

        dragState.current.originalPositions.clear();
        dragState.current.currentOffset.set(0, 0);
        dragState.current.lastUpdateTime = performance.now();
        dragState.current.isDragging = true;
        dragState.current.alignmentSnap = undefined;
        dragState.current.lastValidCollisionOffset = new THREE.Vector2(0, 0);
        dragState.current.selectionIds = new Set(objectIds);

        // Store original positions (now protected by translate mode)
        editorStore.selections.forEach((selection) => {
            if (isAreaFillSymbolType(selection)) {
                const liveFill = floorplannerStore.symbolsMap.get(selection.id);
                if (liveFill && isAreaFillSymbolType(liveFill) && isAreaFillMovementLocked(liveFill)) {
                    return;
                }
            }
            if (isSymbolType(selection)) {
                const symbol = floorplannerStore.symbolsMap.get(selection.id);
                if (symbol) {
                    dragState.current.originalPositions.set(selection.id, {
                        position: symbol.position.clone()
                    });
                }
            } else if (isWallType(selection) || isSingleLineType(selection) || isRulerLineType(selection)) {
                const object = floorplannerStore.findObjectId(selection.id);
                if (object) {
                    dragState.current.originalPositions.set(selection.id, {
                        start: object.start.clone(),
                        end: object.end.clone()
                    });
                }
            }
        });

        const selectionBounds = getSelectionBounds(editorStore.selections.filter(isSelectionBoundsItem));
        if (selectionBounds) {
            dragState.current.selectionBounds = {
                corners: selectionBounds.corners.map((corner: THREE.Vector2) => corner.clone()) as [
                    THREE.Vector2,
                    THREE.Vector2,
                    THREE.Vector2,
                    THREE.Vector2
                ],
                center: selectionBounds.center.clone(),
            };
        } else {
            dragState.current.selectionBounds = undefined;
        }

        // OPTIMIZATION: Skip beginWallDrag for group translation
        // For simple translation, we don't need the heavy overhead of wall drag setup
        // Connections are handled via bypassEdgeConstraints during update
        /*
        wallIds.forEach(wallId => {
            floorplannerStore.beginWallDrag(wallId);
        });
        */

        const onPointerMove = (moveEvent: PointerEvent) => {
            if (aboutToDrag.current) {
                aboutToDrag.current = false;
            }

            const [currentWorldX, currentWorldY] = projectToWorld(
                moveEvent.clientX,
                moveEvent.clientY,
                gl,
                camera
            );

            const deltaX = currentWorldX - initialPointerPosition.current![0];
            const deltaY = currentWorldY - initialPointerPosition.current![1];
            dragState.current.currentOffset.set(deltaX, deltaY);

            const hasMoved = Math.abs(deltaX) > 0.01 || Math.abs(deltaY) > 0.01;
            if (!isDraggingGroupSelection.current && hasMoved) {
                isDraggingGroupSelection.current = true;
                if (!beginWallDragCalled.current && draggedWallIds.current.length > 0) {
                    const selectedWallIds = new Set(draggedWallIds.current);
                    if (rigidGroupTranslate) {
                        // Keep internal joints. Only cut links to walls outside this selection
                        // so overlapping copies don't stay glued, then slide as one piece.
                        floorplannerStore.disconnectSelectionFromExternalConnections(selectedWallIds);
                    } else {
                        beginWallDragCalled.current = true;
                        floorplannerStore.deferConnectionOperations = false;
                        renderStore.allowVisualUpdatesForWall(draggedWallIds.current[0]);
                        // Defer heavy geometry rebuild to endWallDrag (avoids pointermove freezes).
                        floorplannerStore.beginWallDrag(draggedWallIds.current[0], {
                            disconnectConnections: true,
                            deferDetachedWallRebuild: true,
                        });
                        floorplannerStore.deferConnectionOperations = true;
                    }
                }
            }

            const dragPoint = new THREE.Vector2(currentWorldX, currentWorldY);
            const selectionBounds = dragState.current.selectionBounds;
            const selectionIds = dragState.current.selectionIds;

            if (
                !rigidGroupTranslate &&
                selectionBounds &&
                selectionIds &&
                !editorStore.isShiftPressed
            ) {
                const baseOffset = dragState.current.currentOffset;
                const movedCorners = selectionBounds.corners.map((corner) => corner.clone().add(baseOffset));
                const movedCenter = selectionBounds.center.clone().add(baseOffset);

                updateAlignmentLinesForSelection(movedCorners, movedCenter, dragPoint, selectionIds);

                const snapPoints = editorStore.isAltPressed ? [movedCenter] : movedCorners;
                const bestSnap = getGroupAlignmentSnap(snapPoints, dragState.current.alignmentSnap);

                if (bestSnap) {
                    dragState.current.currentOffset.add(bestSnap.delta);
                    dragState.current.alignmentSnap = bestSnap.state;

                    const snappedCorners = selectionBounds.corners.map((corner) =>
                        corner.clone().add(dragState.current.currentOffset)
                    );
                    const snappedCenter = selectionBounds.center.clone().add(dragState.current.currentOffset);
                    updateAlignmentLinesForSelection(snappedCorners, snappedCenter, dragPoint, selectionIds);
                } else {
                    dragState.current.alignmentSnap = undefined;
                }
            } else {
                dragState.current.alignmentSnap = undefined;
                renderStore.clearAlignmentLines();
            }

            const hasStructuralSelection = editorStore.selections.some((selection) =>
                isWallType(selection) || isSingleLineType(selection) || isRulerLineType(selection)
            );
            const looseSymbols = editorStore.selections.filter((selection): selection is SymbolType =>
                isSymbolType(selection) &&
                isLoose2DDragCollisionSymbol(selection) &&
                !floorplannerStore.symbolIsAttached(selection.id)
            );
            if (!hasStructuralSelection && looseSymbols.length > 0) {
                const bypassCollision = editorStore.isShiftPressed;
                const lastValidCollisionOffset = dragState.current.lastValidCollisionOffset ?? new THREE.Vector2(0, 0);
                const resolvedOffset = resolveSymbolGroup2DDragOffset(
                    looseSymbols,
                    dragState.current.originalPositions,
                    dragState.current.currentOffset,
                    lastValidCollisionOffset,
                    {
                        walls: Array.from(floorplannerStore.wallsMap.values()),
                        singleLines: Array.from(floorplannerStore.singleLinesMap.values()),
                        wallShapesById: renderStore.innerWallShapesMap,
                        defaultWallWidth: floorplannerStore.wallWidth,
                        defaultSingleLineWidth: Math.max(
                            floorplannerStore.convertLineWeightToWorld(floorplannerStore.singleLineWeight),
                            0.08
                        ),
                        wallSnapTolerance: editorStore.interactionSnappingTolerance(
                            editorStore.snappingTolerance * 2
                        ),
                    },
                    { bypassCollision }
                );
                dragState.current.currentOffset.copy(resolvedOffset);
                if (!bypassCollision) {
                    dragState.current.lastValidCollisionOffset = resolvedOffset.clone();
                }
            } else {
                dragState.current.lastValidCollisionOffset = undefined;
            }

            // OPTIMIZED: Only update the translate offset, not MobX observables
            // This triggers a render but skips geometry recalculation
            renderStore.updateGroupTranslateOffset(
                dragState.current.currentOffset.x,
                dragState.current.currentOffset.y
            );
        }

        const onPointerUp = async () => {
            if (rafId.current) {
                cancelAnimationFrame(rafId.current);
                rafId.current = null;
            }

            dragState.current.isDragging = false;

            // Get final offset before ending translate mode
            const finalOffsetX = dragState.current.currentOffset.x;
            const finalOffsetY = dragState.current.currentOffset.y;
            const movedEnoughToCommit =
                isDraggingGroupSelection.current ||
                Math.hypot(finalOffsetX, finalOffsetY) > 0.01;

            // Re-enable updates
            floorplannerStore.setBlockDirty(false);

            if (!movedEnoughToCommit) {
                renderStore.endGroupTranslate();
                floorplannerStore.setDeferRoomUpdates(false, { processPending: false });
                floorplannerStore.cancelUndoTransaction?.();
                draggedWallIds.current = [];
                cleanup();
                return;
            }

            // Disable progressive mode for all dragged objects and trigger full update
            const { wallUpdateManager } = await import('../utils/wallUpdateManager');
            editorStore.selections.forEach((selection) => {
                if (isWallType(selection) || isSingleLineType(selection) || isRulerLineType(selection)) {
                    wallUpdateManager.enableProgressiveModeForWall(selection.id, false);
                }
            });

            // NOW commit final positions to MobX in one batch
            // IMPORTANT: Update walls FIRST, then symbols - so symbol attachment calculations use new wall positions
            // NOTE: Keep translate mode active during position commit to prevent flash at original position
            runInAction(() => {
                // First pass: Update all walls/lines
                editorStore.selections.forEach((selection) => {
                    const originalPos = dragState.current.originalPositions.get(selection.id);
                    if (!originalPos) return;

                    if ((isWallType(selection) || isSingleLineType(selection) || isRulerLineType(selection)) && originalPos.start && originalPos.end) {
                        floorplannerStore.updateObjectPosition(selection.id,
                            new THREE.Vector2(
                                originalPos.start.x + finalOffsetX,
                                originalPos.start.y + finalOffsetY
                            ),
                            new THREE.Vector2(
                                originalPos.end.x + finalOffsetX,
                                originalPos.end.y + finalOffsetY
                            ),
                            { bypassEdgeConstraints: true, skipInvalidation: true }
                        );
                        floorplannerStore.markObjectForRoomUpdate(selection.id);
                    }
                });

                if (rigidGroupTranslate && (Math.abs(finalOffsetX) > 1e-9 || Math.abs(finalOffsetY) > 1e-9)) {
                    const delta = new THREE.Vector2(finalOffsetX, finalOffsetY);
                    draggedWallIds.current.forEach((wallId) => {
                        const wall = floorplannerStore.wallsMap.get(wallId);
                        if (!wall || !isWallType(wall) || !wall.edgeConnections?.length) return;
                        wall.edgeConnections = wall.edgeConnections.map((connection) => ({
                            ...connection,
                            sourceEdgePoint: new THREE.Vector2(
                                connection.sourceEdgePoint.x + delta.x,
                                connection.sourceEdgePoint.y + delta.y,
                            ),
                            targetEdgePoint: new THREE.Vector2(
                                connection.targetEdgePoint.x + delta.x,
                                connection.targetEdgePoint.y + delta.y,
                            ),
                        }));
                        floorplannerStore.wallsMap.set(wallId, wall);
                    });
                }

                // Second pass: Update symbols
                editorStore.selections.forEach((selection) => {
                    const originalPos = dragState.current.originalPositions.get(selection.id);
                    if (!originalPos) return;

                    if (isSymbolType(selection) && originalPos.position) {
                        const attachedWall = floorplannerStore.symbolIsAttached(selection.id);

                        // If symbol is attached to a wall that's also being dragged,
                        // the wall attachment distance stays correct as the wall moves.
                        const attachedWallIsBeingDragged = attachedWall && draggedWallIds.current.includes(attachedWall);

                        const newPosition: [number, number] = [
                            originalPos.position.x + finalOffsetX,
                            originalPos.position.y + finalOffsetY,
                        ];
                        if (attachedWall && !attachedWallIsBeingDragged) {
                            floorplannerStore.updateSymbolPositionRespectingAttachment(selection.id, newPosition);
                        } else {
                            floorplannerStore.updateSymbolProperty(selection.id, "position", newPosition);
                        }
                    }
                });
            });

            const hadWallDrag = beginWallDragCalled.current && draggedWallIds.current.length > 0;
            const affectedWallIds = new Set<string>();

            if (hadWallDrag) {
                // Allow snapping/reconnect logic to run after group drag
                renderStore.setIsDragging(false);
                // Avoid stale nearbyWallIds limiting snap targets after group moves
                floorplannerStore.wallDragState.forEach((state) => {
                    if (state.nearbyWallIds) {
                        state.nearbyWallIds = undefined;
                    }
                });
                draggedWallIds.current.forEach((wallId) => {
                    const impacted = floorplannerStore.endWallDrag(wallId);
                    impacted.forEach((id) => affectedWallIds.add(id));
                    affectedWallIds.add(wallId);
                });
                beginWallDragCalled.current = false;
            }

            const { updateWallClippings, updateWallConnections } = await import('../components/FloorPlan/updateWallConnections');

            if (hadWallDrag) {
                const updatedWalls = Array.from(affectedWallIds);
                draggedWallIds.current.forEach((wallId) => {
                    updateWallConnections(wallId, null, [wallId, ...updatedWalls], floorplannerStore);
                });

                if (typeof wallUpdateManager.processPendingUpdatesNow === 'function') {
                    try {
                        await wallUpdateManager.processPendingUpdatesNow();
                    } catch (error) {
                        console.error('useDragSelectedObjects: Failed to flush wall updates after group drag', error);
                    }
                }
            }

            // Force recompose wall shapes and update clippings BEFORE ending translate mode
            // This prevents a flash where walls appear at original positions:
            // 1. Recompose updates geometry to new MobX positions (while offset still applied)
            // 2. Then clear offset immediately after, before React renders
            // 3. React sees: new geometry + 0 offset = correct position
            const wallsToRecompose = new Set<string>(draggedWallIds.current);
            if (hadWallDrag) {
                affectedWallIds.forEach((wallId) => wallsToRecompose.add(wallId));
            }

            wallsToRecompose.forEach(wallId => {
                // Update wall clippings for symbol attachments (doors/windows)
                // This must be done BEFORE recomposing to ensure holes are correctly applied
                updateWallClippings(wallId);

                renderStore.clearWallShapes(wallId);
                wallGeometryCache.clearWall(wallId);
            });

            // Compose after all walls are cleared so trimming doesn't use stale connected shapes
            wallsToRecompose.forEach(wallId => {
                renderStore.composeWallShape(wallId, undefined, false);
            });

            if (hadWallDrag) {
                draggedWallIds.current.forEach((wallId) => {
                    renderStore.disallowVisualUpdatesForWall(wallId);
                });
            }

            // End translate mode AFTER wall shapes are recomposed AND room centroids updated
            // This ensures walls and room labels don't flash at original positions
            renderStore.endGroupTranslate();

            const wallsForRoomSync = [...draggedWallIds.current];
            const processDeferredRoomUpdates = () => {
                floorplannerStore.setDeferRoomUpdates(false, { processPending: false });
                // Keep room-bound floors glued to moved walls even when locked
                // floors were excluded from the drag commit.
                if (wallsForRoomSync.length > 0) {
                    floorplannerStore.syncAreaFillsForObjects(wallsForRoomSync);
                }
                floorplannerStore.updateAffectedRooms();
            };
            if ('requestIdleCallback' in window) {
                requestIdleCallback(processDeferredRoomUpdates);
            } else {
                setTimeout(processDeferredRoomUpdates, 0);
            }

            // Restore connections and snapping for any walls we temporarily disconnected
            /*
            if (draggedWallIds.current.length > 0) {
                const affected = new Set<string>();
                draggedWallIds.current.forEach(id => {
                    const impacted = floorplannerStore.endWallDrag(id);
                    impacted.forEach(w => affected.add(w));
                });
                affected.forEach(wallId => {
                    floorplannerStore.objectNeedsUpdate(wallId);
                });
                draggedWallIds.current = [];
            }
            */

            // Mark as dirty after the action completes (room updates already processed above)
            floorplannerStore.setDirty();

            // Record final state of all dragged objects for undo
            const changedAttachmentWallIds = new Set<string>();
            editorStore.selections.forEach((selection) => {
                if (isSymbolType(selection)) {
                    const attachedWall = floorplannerStore.symbolIsAttached(selection.id);
                    if (attachedWall && !draggedWallIds.current.includes(attachedWall)) {
                        changedAttachmentWallIds.add(attachedWall);
                    }
                    floorplannerStore.recordSymbolChange(selection.id, 'modify');
                } else {
                    floorplannerStore.recordObjectChange(selection.id, 'modify');
                }
            });
            changedAttachmentWallIds.forEach((wallId) => {
                floorplannerStore.recordWallChange(wallId, 'modify');
            });
            floorplannerStore.pushToUndoStack();

            cleanup();
        };

        // Handler for ESC key to cancel the drag
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                const cancelDrag = async () => {
                    const hadWallDrag = beginWallDragCalled.current && draggedWallIds.current.length > 0;
                    const affectedWallIds = new Set<string>();

                    if (hadWallDrag) {
                        renderStore.setIsDragging(false);
                        // Avoid stale nearbyWallIds limiting snap targets after group moves
                        floorplannerStore.wallDragState.forEach((state) => {
                            if (state.nearbyWallIds) {
                                state.nearbyWallIds = undefined;
                            }
                        });
                        draggedWallIds.current.forEach((wallId) => {
                            const impacted = floorplannerStore.endWallDrag(wallId);
                            impacted.forEach((id) => affectedWallIds.add(id));
                            affectedWallIds.add(wallId);
                        });
                        beginWallDragCalled.current = false;

                        const { updateWallClippings, updateWallConnections } = await import('../components/FloorPlan/updateWallConnections');
                        const updatedWalls = Array.from(affectedWallIds);
                        draggedWallIds.current.forEach((wallId) => {
                            updateWallConnections(wallId, null, [wallId, ...updatedWalls], floorplannerStore);
                        });

                        const wallsToRecompose = new Set<string>(draggedWallIds.current);
                        affectedWallIds.forEach((wallId) => wallsToRecompose.add(wallId));
                        wallsToRecompose.forEach((wallId) => {
                            updateWallClippings(wallId);
                            renderStore.clearWallShapes(wallId);
                            wallGeometryCache.clearWall(wallId);
                        });
                        wallsToRecompose.forEach((wallId) => {
                            renderStore.composeWallShape(wallId, undefined, false);
                        });

                        draggedWallIds.current.forEach((wallId) => {
                            renderStore.disallowVisualUpdatesForWall(wallId);
                        });
                    }

                    // Cancel the translate without committing
                    renderStore.endGroupTranslate();
                    floorplannerStore.setDeferRoomUpdates(false, { processPending: false });
                    floorplannerStore.setBlockDirty(false);

                    draggedWallIds.current = [];

                    // Cancel the undo transaction
                    floorplannerStore.cancelUndoTransaction?.();

                    cleanup();
                };

                void cancelDrag();
            }
        };

        // Handler for window blur to cancel the drag
        const onBlur = () => {
            if (renderStore.isGroupTranslating) {
                const cancelDrag = async () => {
                    const hadWallDrag = beginWallDragCalled.current && draggedWallIds.current.length > 0;
                    const affectedWallIds = new Set<string>();

                    if (hadWallDrag) {
                        renderStore.setIsDragging(false);
                        // Avoid stale nearbyWallIds limiting snap targets after group moves
                        floorplannerStore.wallDragState.forEach((state) => {
                            if (state.nearbyWallIds) {
                                state.nearbyWallIds = undefined;
                            }
                        });
                        draggedWallIds.current.forEach((wallId) => {
                            const impacted = floorplannerStore.endWallDrag(wallId);
                            impacted.forEach((id) => affectedWallIds.add(id));
                            affectedWallIds.add(wallId);
                        });
                        beginWallDragCalled.current = false;

                        const { updateWallClippings, updateWallConnections } = await import('../components/FloorPlan/updateWallConnections');
                        const updatedWalls = Array.from(affectedWallIds);
                        draggedWallIds.current.forEach((wallId) => {
                            updateWallConnections(wallId, null, [wallId, ...updatedWalls], floorplannerStore);
                        });

                        const wallsToRecompose = new Set<string>(draggedWallIds.current);
                        affectedWallIds.forEach((wallId) => wallsToRecompose.add(wallId));
                        wallsToRecompose.forEach((wallId) => {
                            updateWallClippings(wallId);
                            renderStore.clearWallShapes(wallId);
                            wallGeometryCache.clearWall(wallId);
                        });
                        wallsToRecompose.forEach((wallId) => {
                            renderStore.composeWallShape(wallId, undefined, false);
                        });

                        draggedWallIds.current.forEach((wallId) => {
                            renderStore.disallowVisualUpdatesForWall(wallId);
                        });
                    }

                    // Cancel the translate without committing
                    renderStore.endGroupTranslate();
                    floorplannerStore.setDeferRoomUpdates(false, { processPending: false });
                    floorplannerStore.setBlockDirty(false);

                    draggedWallIds.current = [];

                    // Cancel the undo transaction
                    floorplannerStore.cancelUndoTransaction?.();

                    cleanup();
                };

                void cancelDrag();
            }
        };

        // Cleanup function to remove all event listeners
        const cleanup = () => {
            window.removeEventListener("pointermove", onPointerMove);
            window.removeEventListener("pointerup", onPointerUp);
            window.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("blur", onBlur);
            isDraggingGroupSelection.current = false;
            aboutToDrag.current = true;
            beginWallDragCalled.current = false;
            renderStore.clearAlignmentLines();
            dragState.current.alignmentSnap = undefined;
            dragState.current.selectionBounds = undefined;
            dragState.current.selectionIds = undefined;
            dragState.current.lastValidCollisionOffset = undefined;
        };

        window.addEventListener("pointermove", onPointerMove);
        window.addEventListener("pointerup", onPointerUp);
        window.addEventListener("keydown", onKeyDown);
        window.addEventListener("blur", onBlur);
    }, [gl, camera]);

    const onDragStart = useCallback(() => {
        renderStore.setIsDragging(true);
        renderStore.setBlockDirty(true);
    }, [renderStore]);

    const onDragEnd = useCallback(() => {
        renderStore.setIsDragging(false);
        renderStore.setBlockDirty(false);
        renderStore.setDirty();
    }, [renderStore]);

    return { startDraggingGroupSelection, isDraggingGroupSelection };
};
